import { useState, useRef, useEffect } from 'react';
import { Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, Typography, TextField, Tabs, Tab, Container } from '@mui/material';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Template, Settings } from '../types';
import { imageToText, generateDescription, generateImage } from '../services/openai';
import { openDB } from 'idb';
import { defaultSettings } from '../constants/settings';

export default function MainPage() {
  const [templates] = useLocalStorage<Template[]>('templates', []);
  const [settings] = useLocalStorage<Settings>('settings', defaultSettings);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [resultImage, setResultImage] = useState<string>('');
  const [resultDescription, setResultDescription] = useState<string>('');
  const [inputText, setInputText] = useState<string>('');
  const [hasCameraPermission, setHasCameraPermission] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<number>(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (templates.length > 0 && !selectedTemplate) {
      setSelectedTemplate(templates[0].name);
    }
  }, [templates, selectedTemplate]);

  useEffect(() => {
    // Initialize camera
    const initCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
        }
      } catch (err) {
        console.error('Error accessing camera:', err);
        setHasCameraPermission(false);
      }
    };

    if (activeTab === 0 && hasCameraPermission) {
      initCamera();
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [activeTab, hasCameraPermission]);

  const saveToHistory = async (image: string, description: string) => {
    try {
      const db = await openDB('minds-eye', 1);
      await db.add('history', {
        id: crypto.randomUUID(),
        image,
        description,
        date: new Date().toISOString(),
        template: selectedTemplate
      });
    } catch (error) {
      console.error('Error saving to history:', error);
    }
  };

  const captureImage = async () => {
    if (!videoRef.current || !settings.openaiKey) return;

    setIsProcessing(true);
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      const base64Image = canvas.toDataURL('image/jpeg');
      
      try {
        // Step 1: Convert image to text
        const text = await imageToText(base64Image, settings);
        setInputText(text); // Update the textbox with the captured text
        setActiveTab(1); // Switch to the text tab
      } catch (error) {
        console.error('Error processing image:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleGenerate = async () => {
    if (!inputText.trim() || !settings.openaiKey) return;

    setIsProcessing(true);
    try {
      // Step 2: Generate description
      const description = await generateDescription(inputText, settings);
      setResultDescription(description);
      
      // Step 3: Generate image
      const selectedTemplateObj = templates.find(t => t.name === selectedTemplate);
      let prompt = settings.systemPrompt;
      if (selectedTemplateObj && selectedTemplateObj.prompt && selectedTemplateObj.prompt.trim().length > 0) {
        prompt += `\n\n${selectedTemplateObj.prompt}`;
      }
      prompt += `\n\n${inputText}`;
      
      const generatedImage = await generateImage(prompt, settings);
      setResultImage(generatedImage);
      
      // Step 4: Save to history
      await saveToHistory(generatedImage, description);
      
      setShowResult(true);
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        <FormControl fullWidth>
          <InputLabel>Template</InputLabel>
          <Select
            value={selectedTemplate}
            label="Template"
            onChange={(e) => setSelectedTemplate(e.target.value)}
          >
            {templates.map((template) => (
              <MenuItem key={template.name} value={template.name}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
      }}>
        {!showResult ? (
          <>
            <Tabs 
              value={activeTab} 
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Camera" disabled={!hasCameraPermission} />
              <Tab label="Text" />
            </Tabs>

            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}>
              {activeTab === 0 && hasCameraPermission ? (
                <Box sx={{ 
                  flex: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2
                }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      objectFit: 'cover',
                      flex: 1
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={captureImage}
                    disabled={isProcessing || !settings.imageRouterKey}
                    sx={{ flex: 1, mt: 2 }}
                  >
                    Capture Text
                  </Button>
                  {isProcessing && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.5)'
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 2,
                }}>
                  <TextField
                    sx={{ 
                      maxHeight: '75%',
                      overflow: 'auto'
                    }}
                    fullWidth
                    maxRows={10}
                    multiline
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="Enter a description of what you want to visualize by typing it or using your phone's voice mode. You can also capture text from your camera."
                  />
                  <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={isProcessing || !inputText.trim() || !settings.imageRouterKey}
                    sx={{ flex: 0, mt: 2 }}
                  >
                    See Your Mind's Eye
                  </Button>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, 
            display: 'flex',
            flexDirection: 'column' }}>
            <img 
              src={resultImage} 
              alt="Generated" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <Box sx={{ 
              position: 'absolute', 
              top: 16, 
              left: 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.7)',
              p: 2,
              borderRadius: 1
            }}>
              <Typography variant="body2" color="white">
                {resultDescription}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setShowResult(false)}
              sx={{ position: 'absolute', top: 16, left: 16 }}
            >
              Back
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
} 